import { forwardRef, useId } from "react"

import { cn } from "@/lib/utils"
import * as CheckboxPrimitive from "@radix-ui/react-checkbox"
import * as LabelPrimitive from "@radix-ui/react-label"
import { IconCheck, IconMinus } from "@tabler/icons-react"

import { Input, InputProps } from "./Input"

export type CheckboxProps = {
  label?: string
  id?: string
  labelClassName?: string
} & CheckboxPrimitive.CheckboxProps

export const Checkbox = forwardRef(function Checkbox(
  { label, className, checked, labelClassName, ...props }: CheckboxProps,
  ref: React.Ref<HTMLInputElement>,
) {
  if ((props as any).onChange) {
    throw new Error(
      "You passed onChange={...} to Checkbox. You want onCheckedChange={...}.",
    )
  }

  // When a label is provided we always use an internally-generated ID so
  // that it is guaranteed to be unique.
  const labelId = useId()
  const forId = label ? labelId : props.id

  return (
    <div className={cn("relative flex items-center", className)}>
      <CheckboxPrimitive.Root
        className={cn(
          "peer flex h-4 w-4 shrink-0 items-center justify-center rounded border",
          "focus-outline",
          "border-grey-400",
          "enabled:aria-checked:border-colors-icon-brand enabled:aria-checked:bg-colors-icon-brand",
          "disabled:border-colors-border-disabled disabled:bg-grey-300",
          "disabled:aria-checked:border-grey-300 disabled:aria-checked:bg-grey-300",
        )}
        checked={checked !== undefined ? Boolean(checked) : undefined}
        {...props}
        id={forId}
      >
        <CheckboxPrimitive.Indicator
          ref={ref}
          className="flex items-center justify-center text-white"
        >
          {checked === "indeterminate" ? (
            <IconMinus size={12} stroke={3} />
          ) : (
            <IconCheck size={12} stroke={3} />
          )}
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>
      {label ? (
        <LabelPrimitive.Root
          htmlFor={forId}
          className={cn(
            "pl-2 select-none peer-enabled:cursor-pointer",
            "peer-enabled:text-colors-text-primary peer-disabled:text-colors-text-placeholder",
            labelClassName,
          )}
        >
          <span className="text-body1 text-current">{label}</span>
        </LabelPrimitive.Root>
      ) : null}
    </div>
  )
})

export const CheckboxOther = forwardRef(function CheckboxOther(
  {
    label = "Other:",
    value,
    onChange,
    placeholder,
    ...props
  }: Omit<CheckboxProps, "value" | "onChange"> & {
    id: string
    value: InputProps["value"]
    onChange: InputProps["onChange"]
    placeholder?: InputProps["placeholder"]
  },
  ref: React.Ref<HTMLInputElement>,
) {
  return (
    <div className="flex flex-col gap-3">
      <Checkbox label={label} {...props} ref={ref} />
      <Input value={value} onChange={onChange} placeholder={placeholder} />
    </div>
  )
})
